import * as React from "react"
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { TableHead } from "@/components/ui/table"
import type { SortDir } from "./useTableSort"

export interface SortableThProps {
  /** Column key — passed to onSort, and (when resizable) to onResize. */
  sortKey: string
  /** Currently active sort key (from useTableSort). */
  currentKey: string | null
  /** Current sort direction (from useTableSort). */
  dir: SortDir
  onSort: (key: string) => void
  className?: string
  children: React.ReactNode
  /** Render a right-edge drag handle. Requires onResize. */
  resizable?: boolean
  /** Fixed width in px (from useColumnResize.widths). */
  width?: number
  /** Wire to useColumnResize.startResize. */
  onResize?: (key: string, event: React.PointerEvent<HTMLElement>) => void
  /** Grow into leftover table width. minWidth still applies; maxWidth does not. */
  fill?: boolean
}

/**
 * Sortable (and optionally resizable) header cell built on TableHead.
 * Click / Enter / Space toggles sort; dragging the right-edge handle
 * resizes without triggering sort.
 */
export function SortableTh({
  sortKey,
  currentKey,
  dir,
  onSort,
  className,
  children,
  resizable = false,
  width,
  onResize,
  fill = false,
}: SortableThProps) {
  const active = currentKey === sortKey
  const showHandle = resizable && Boolean(onResize)

  const caret = active ? (
    dir === "asc" ? (
      <ChevronUp className="h-3.5 w-3.5 shrink-0" />
    ) : (
      <ChevronDown className="h-3.5 w-3.5 shrink-0" />
    )
  ) : (
    <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
  )

  return (
    <TableHead
      className={cn(
        "relative cursor-pointer select-none overflow-hidden hover:bg-muted/50",
        active && "text-foreground",
        className
      )}
      style={
        width === undefined
          ? undefined
          : fill
            ? { minWidth: width }
            : { width, minWidth: width, maxWidth: width }
      }
      role="button"
      tabIndex={0}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      onClick={() => onSort(sortKey)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onSort(sortKey)
        }
      }}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {caret}
      </span>
      {showHandle && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={`Resize ${typeof children === "string" ? children : sortKey} column`}
          className="absolute right-0 top-1/2 h-5 w-2 -translate-y-1/2 cursor-col-resize rounded hover:bg-teal/20"
          onPointerDown={(event) => onResize?.(sortKey, event)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        />
      )}
    </TableHead>
  )
}
